import React, { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon } from "../icons";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// Matches the style of existing default passwords (blueeagle, redfalcon, etc.)
const PW_COLORS = ["blue", "red", "green", "pink", "amber", "violet", "coral", "indigo", "teal", "olive", "rust", "navy"];
const PW_ANIMALS = ["eagle", "falcon", "turtle", "monkey", "otter", "panther", "wolf", "hawk", "bison", "lynx", "moose", "raven", "fox", "shark", "tiger"];
function randomPassword() {
  const c = PW_COLORS[Math.floor(Math.random() * PW_COLORS.length)];
  const a = PW_ANIMALS[Math.floor(Math.random() * PW_ANIMALS.length)];
  return `${c}${a}`;
}

function CellInput({ value, onChange, placeholder, type = "text", style: s }) {
  return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...S.cellInput, ...s }} />;
}

function SortArrow({ sortCol, col, sortDir }) {
  if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>&#8597;</span>;
  return <span style={{ marginLeft: 4, color: "#0033FF" }}>{sortDir === "asc" ? "\u2191" : "\u2193"}</span>;
}

function AddEmployeeModal({ existingEmployees, onCreate, onClose }) {
  // Suggest the next sequential employee ID (zero-padded to 2 digits)
  const suggestedId = useMemo(() => {
    const nums = existingEmployees
      .map((e) => parseInt(String(e.empId || "").replace(/\D/g, ""), 10))
      .filter((n) => !isNaN(n));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    return String(next).padStart(2, "0");
  }, [existingEmployees]);

  const [form, setForm] = useState({
    empId: suggestedId,
    name: "",
    position: "",
    rate: "",
    startDate: "",
    birthday: "",
    role: "employee",
    printfarmRole: "",
    password: randomPassword(),
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.empId.trim() && form.name.trim() && form.position.trim() && form.password.trim();

  const submit = () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    onCreate({
      ...form,
      empId: form.empId.trim().padStart(2, "0"),
      rate: parseFloat(form.rate) || 0,
    });
  };

  const label = (text) => (
    <div style={{
      fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.1em", textTransform: "uppercase",
      color: "rgba(10,10,15,0.58)", marginBottom: 6,
    }}>{text}</div>
  );
  const input = (props) => (
    <input
      {...props}
      style={{
        width: "100%", padding: "10px 12px", borderRadius: 0,
        border: "2px solid #0A0A0F", background: "#FFFFFF", color: "#0A0A0F",
        fontSize: 14, fontFamily: "'Archivo', sans-serif", outline: "none",
      }}
    />
  );

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(10,10,15,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 96vw)", maxHeight: "92vh",
          background: "#F4F2EC", border: "2px solid #0A0A0F",
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "8px 8px 0 #0033FF",
        }}
      >
        <div style={{
          padding: "18px 22px", borderBottom: "2px solid #0A0A0F",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: "#0033FF", fontWeight: 500, marginBottom: 6,
            }}>\u00b7 NEW EMPLOYEE \u00b7 CREATE ACCOUNT</div>
            <h2 style={{
              fontFamily: "'Archivo Black', 'Archivo', sans-serif",
              fontSize: 26, fontWeight: 900, textTransform: "uppercase",
              letterSpacing: "-0.02em", lineHeight: 0.95, margin: 0, color: "#0A0A0F",
            }}>Add Employee</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: "2px solid #0A0A0F",
              width: 32, height: 32, cursor: "pointer", color: "#0A0A0F",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <XIcon />
          </button>
        </div>

        <div style={{ padding: 22, overflowY: "auto" }}>
          {/* Info row 1 */}
          <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 14, marginBottom: 14 }}>
            <div>
              {label("ID *")}
              {input({ value: form.empId, onChange: (e) => set("empId", e.target.value), placeholder: "05" })}
            </div>
            <div>
              {label("Full Name *")}
              {input({ value: form.name, onChange: (e) => set("name", e.target.value), placeholder: "Jane Doe", autoFocus: true })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 14, marginBottom: 14 }}>
            <div>
              {label("Position *")}
              {input({ value: form.position, onChange: (e) => set("position", e.target.value), placeholder: "Fulfillment Specialist" })}
            </div>
            <div>
              {label("Hourly Rate")}
              {input({ type: "number", value: form.rate, onChange: (e) => set("rate", e.target.value), placeholder: "20.00" })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 22 }}>
            <div>
              {label("Start Date")}
              {input({ type: "date", value: form.startDate, onChange: (e) => set("startDate", e.target.value) })}
            </div>
            <div>
              {label("Birthday")}
              {input({ type: "date", value: form.birthday, onChange: (e) => set("birthday", e.target.value) })}
            </div>
          </div>

          {/* Account section */}
          <div style={{
            padding: "16px 18px", border: "2px solid #0A0A0F",
            background: "rgba(0,51,255,0.04)", marginBottom: 4,
          }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: "#0033FF", fontWeight: 700, marginBottom: 14,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ width: 6, height: 6, background: "#0033FF", display: "inline-block" }} />
              LOGIN ACCOUNT
            </div>

            <div style={{ marginBottom: 14 }}>
              {label("Role")}
              <div style={{ display: "flex", gap: 0, border: "2px solid #0A0A0F" }}>
                {[
                  { v: "employee", l: "Employee", d: "Default: time-clock only access" },
                  { v: "admin", l: "Admin", d: "Full access to all sections" },
                ].map((opt, i) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => set("role", opt.v)}
                    title={opt.d}
                    style={{
                      flex: 1, padding: "10px 14px",
                      background: form.role === opt.v ? "#0A0A0F" : "transparent",
                      color: form.role === opt.v ? "#FFFFFF" : "#0A0A0F",
                      border: "none", borderLeft: i === 0 ? "none" : "2px solid #0A0A0F",
                      fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13,
                      letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer",
                    }}
                  >{opt.l}</button>
                ))}
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                color: "rgba(10,10,15,0.58)", marginTop: 6, letterSpacing: "0.04em",
              }}>
                {form.role === "admin"
                  ? "Will get permissions for every section."
                  : "Will get time-clock only. Adjust later in Settings."}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              {label("PrintFarm Role")}
              <select
                value={form.printfarmRole}
                onChange={(e) => set("printfarmRole", e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: 0,
                  border: "2px solid #0A0A0F", background: "#FFFFFF", color: "#0A0A0F",
                  fontSize: 14, fontFamily: "'Archivo', sans-serif", outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="">Default</option>
                <option value="viewer">Viewer</option>
                <option value="operator">Operator</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div>
              {label("Password *")}
              <div style={{ display: "flex", gap: 8 }}>
                {input({
                  value: form.password,
                  onChange: (e) => set("password", e.target.value),
                  placeholder: "bluetuxedo",
                })}
                <button
                  type="button"
                  onClick={() => set("password", randomPassword())}
                  style={{
                    padding: "10px 14px", border: "2px solid #0A0A0F",
                    background: "#FFFFFF", color: "#0A0A0F", cursor: "pointer",
                    fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 12,
                    letterSpacing: "0.06em", textTransform: "uppercase", whiteSpace: "nowrap",
                  }}
                >\u21bb Generate</button>
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                color: "rgba(10,10,15,0.58)", marginTop: 6, letterSpacing: "0.04em",
                lineHeight: 1.5,
              }}>
                Employee logs in with ID <strong style={{ color: "#0A0A0F" }}>{form.empId || "\u2014"}</strong> and this password.
                Share these credentials with them after creating the account.
              </div>
            </div>
          </div>
        </div>

        <div style={{
          padding: "14px 22px", borderTop: "2px solid #0A0A0F",
          display: "flex", gap: 10, justifyContent: "flex-end", background: "#F4F2EC",
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 18px", border: "2px solid #0A0A0F",
              background: "transparent", color: "#0A0A0F", cursor: "pointer",
              fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13,
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}
          >Cancel</button>
          <button
            type="button"
            disabled={!canSubmit || submitting}
            onClick={submit}
            style={{
              padding: "10px 18px", border: "2px solid #0033FF",
              background: "#0033FF", color: "#FFFFFF", cursor: canSubmit ? "pointer" : "not-allowed",
              fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13,
              letterSpacing: "0.06em", textTransform: "uppercase",
              opacity: canSubmit && !submitting ? 1 : 0.4,
            }}
          >{submitting ? "Creating\u2026" : "Create Employee"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function EmployeesPage({ employees, setEmployees, onRefresh }) {
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [createdNotice, setCreatedNotice] = useState(null); // { name, empId, password }
  const [filter, setFilter] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  // Extra time-clock billing accounts an employee can be assigned to (beyond nycmfg).
  const [extraAccounts, setExtraAccounts] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/clock-accounts`).then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setExtraAccounts(d.filter((a) => a.key !== "nycmfg")); })
      .catch(() => {});
  }, []);

  // Who this employee gets a push about when they clock in. Stored on the
  // WATCHER, not the watched: the person who wants to know is the one who
  // opts in, so nobody is signed up for notifications by someone else.
  const toggleClockWatch = (empId) => {
    setEditRow((row) => {
      const cur = Array.isArray(row.clockWatch) ? row.clockWatch : [];
      const next = cur.includes(empId) ? cur.filter((k) => k !== empId) : [...cur, empId];
      return { ...row, clockWatch: next };
    });
  };

  const toggleClockAccount = (key) => {
    setEditRow((row) => {
      const cur = Array.isArray(row.clockAccounts) ? row.clockAccounts : [];
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      return { ...row, clockAccounts: next };
    });
  };

  const filtered = useMemo(() => {
    let l = employees;
    if (filter) {
      const f = filter.toLowerCase();
      l = l.filter((e) => e.name.toLowerCase().includes(f) || e.position.toLowerCase().includes(f) || (e.empId || "").includes(f));
    }
    if (sortCol) {
      l = [...l].sort((a, b) => {
        let va = a[sortCol], vb = b[sortCol];
        if (sortCol === "rate") return sortDir === "asc" ? va - vb : vb - va;
        va = String(va || "").toLowerCase(); vb = String(vb || "").toLowerCase();
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return l;
  }, [employees, filter, sortCol, sortDir]);

  const handleSort = (c) => {
    if (sortCol === c) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(c); setSortDir("asc"); }
  };

  const saveEdit = () => {
    if (!editRow.name || !editRow.position) return;
    // Passwords are write-only on the server (hashed, never returned) — only
    // send one when the admin typed a replacement; blank = unchanged.
    const body = { ...editRow, printfarmRole: editRow.printfarmRole || "" };
    const newPassword = (editRow.password || "").trim();
    if (newPassword) body.password = newPassword;
    else delete body.password;
    fetch(`${API_URL}/api/employees/${editId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => r.json()).then(() => { if (onRefresh) onRefresh(); setEditId(null); });
  };

  const deleteRow = (id) => {
    fetch(`${API_URL}/api/employees/${id}`, { method: "DELETE" })
      .then(() => { if (onRefresh) onRefresh(); });
  };

  const createEmployee = (payload) => {
    fetch(`${API_URL}/api/employees`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((created) => {
        if (onRefresh) onRefresh();
        setShowAddModal(false);
        // Show the credentials so admin can copy + share with the new hire
        if (created && created.empId) {
          setCreatedNotice({
            name: created.name || payload.name,
            empId: created.empId,
            password: payload.password,
            role: created.role || payload.role,
          });
        }
      })
      .catch(() => { setShowAddModal(false); });
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>Employees</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Manage team members and hourly rates</p>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter employees..." style={{ ...S.input, marginBottom: 0, maxWidth: 280 }} />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 15 }}>{employees.length} employee{employees.length !== 1 ? "s" : ""}</span>
          <button onClick={() => setShowAddModal(true)} style={S.addBtn}><PlusIcon /> Add Employee</button>
        </div>
      </div>
      <div style={{ ...S.tableWrap, overflowX: "auto" }}>
        <table style={{ ...S.table, minWidth: 800 }}>
          <thead>
            <tr>
              <th onClick={() => handleSort("empId")} style={{ ...S.th, width: "7%", cursor: "pointer", userSelect: "none" }}>ID<SortArrow sortCol={sortCol} col="empId" sortDir={sortDir} /></th>
              <th onClick={() => handleSort("name")} style={{ ...S.th, width: "16%", cursor: "pointer", userSelect: "none" }}>Name<SortArrow sortCol={sortCol} col="name" sortDir={sortDir} /></th>
              <th onClick={() => handleSort("position")} style={{ ...S.th, width: "18%", cursor: "pointer", userSelect: "none" }}>Position<SortArrow sortCol={sortCol} col="position" sortDir={sortDir} /></th>
              <th onClick={() => handleSort("rate")} style={{ ...S.th, width: "12%", cursor: "pointer", userSelect: "none" }}>Salary/Hr<SortArrow sortCol={sortCol} col="rate" sortDir={sortDir} /></th>
              <th style={{ ...S.th, width: "14%" }}>Start Date</th>
              <th style={{ ...S.th, width: "14%" }}>Birthday</th>
              <th style={{ ...S.th, width: "12%", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <React.Fragment key={e.id}>
              <tr style={editId === e.id ? S.editingRow : {}}>
                {editId === e.id ? (
                  <>
                    <td style={S.td}><CellInput value={editRow.empId || ""} onChange={(v) => setEditRow({ ...editRow, empId: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.name} onChange={(v) => setEditRow({ ...editRow, name: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.position} onChange={(v) => setEditRow({ ...editRow, position: v })} /></td>
                    <td style={S.td}><CellInput value={editRow.rate} onChange={(v) => setEditRow({ ...editRow, rate: v })} type="number" /></td>
                    <td style={S.td}><input type="date" value={editRow.startDate || ""} onChange={(ev) => setEditRow({ ...editRow, startDate: ev.target.value })} style={{ ...S.cellInput, fontSize: 13, colorScheme: "dark" }} /></td>
                    <td style={S.td}><input type="date" value={editRow.birthday || ""} onChange={(ev) => setEditRow({ ...editRow, birthday: ev.target.value })} style={{ ...S.cellInput, fontSize: 13, colorScheme: "dark" }} /></td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={saveEdit} style={S.iconBtn}><CheckIcon /></button>
                      <button onClick={() => setEditId(null)} style={{ ...S.iconBtn, marginLeft: 6 }}><XIcon /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "rgba(10,10,15,0.58)", fontWeight: 600 }}>{e.empId || "—"}</td>
                    <td style={{ ...S.td, fontWeight: 600, color: "#0A0A0F" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>{e.name}</span>
                        {e.role === "admin" && (
                          <span style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "#0033FF",
                            background: "rgba(0,51,255,0.12)",
                            border: "1px solid #0033FF",
                            padding: "2px 6px",
                          }}>Admin</span>
                        )}
                        {Array.isArray(e.clockAccounts) && e.clockAccounts.map((k) => {
                          const lbl = (extraAccounts.find((a) => a.key === k) || {}).label || k;
                          return (
                            <span key={k} style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                              textTransform: "uppercase", color: "#0033FF",
                              background: "rgba(0,51,255,0.12)", border: "1px solid #0033FF",
                              padding: "2px 6px",
                            }}>⏱ {lbl}</span>
                          );
                        })}
                      </div>
                    </td>
                    <td style={S.td}><span style={S.typeBadge}>{e.position}</span></td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0033FF" }}>${(e.rate || 0).toFixed(2)}/hr</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#0A0A0F" }}>{fmtDate(e.startDate)}</td>
                    <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#0A0A0F" }}>{fmtDate(e.birthday)}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={() => { setEditId(e.id); setEditRow({ ...e }); }} style={S.iconBtn}><EditIcon /></button>
                      <button onClick={() => deleteRow(e.id)} style={{ ...S.iconBtn, marginLeft: 6 }}><TrashIcon /></button>
                    </td>
                  </>
                )}
              </tr>
              {editId === e.id && (
                <tr style={S.editingRow}>
                  <td colSpan={7} style={{ ...S.td, paddingTop: 0 }}>
                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                          New Password <span style={{ fontWeight: 400 }}>— blank keeps the current one</span>
                        </div>
                        <CellInput value={editRow.password || ""} onChange={(v) => setEditRow({ ...editRow, password: v })} placeholder="unchanged" style={{ width: 180 }} />
                      </div>
                      <div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                          PrintFarm
                        </div>
                        <select value={editRow.printfarmRole || ""} onChange={(ev) => setEditRow({ ...editRow, printfarmRole: ev.target.value })} style={{ ...S.cellInput, width: 160, cursor: "pointer" }}>
                          <option value="">Default</option>
                          <option value="viewer">Viewer</option>
                          <option value="operator">Operator</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      {extraAccounts.length > 0 && (
                        <div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                            Time-Clock Accounts <span style={{ fontWeight: 400 }}>— lets this employee clock in to a client brand so those hours bill separately</span>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {extraAccounts.map((a) => {
                              const on = Array.isArray(editRow.clockAccounts) && editRow.clockAccounts.includes(a.key);
                              return (
                                <button key={a.key} type="button" onClick={() => toggleClockAccount(a.key)} style={{
                                  padding: "8px 14px", borderRadius: 0, border: "2px solid #0A0A0F",
                                  background: on ? "#0033FF" : "transparent", color: on ? "#fff" : "#0A0A0F",
                                  fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
                                  cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em",
                                }}>{on ? "✓ " : ""}{a.label}</button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                          Clock-In Alerts <span style={{ fontWeight: 400 }}>— send this person a phone notification when the people below clock in</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {employees.filter((o) => o.empId !== editRow.empId).map((o) => {
                            const on = Array.isArray(editRow.clockWatch) && editRow.clockWatch.includes(o.empId);
                            return (
                              <button key={o.empId} type="button" onClick={() => toggleClockWatch(o.empId)} style={{
                                padding: "8px 14px", borderRadius: 0, border: "2px solid #0A0A0F",
                                background: on ? "#0033FF" : "transparent", color: on ? "#fff" : "#0A0A0F",
                                fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
                                cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em",
                              }}>{on ? "✓ " : ""}{o.name}</button>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", marginTop: 6 }}>
                          Needs the NYC Mfg Field app installed and signed in on their phone.
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 40 }}>{filter ? "No match." : "No employees yet."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <AddEmployeeModal
          existingEmployees={employees}
          onCreate={createEmployee}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {createdNotice && (
        <CredentialsNotice notice={createdNotice} onClose={() => setCreatedNotice(null)} />
      )}
    </div>
  );
}

// Modal shown after a new employee is created — admin can copy the
// credentials to share with the new hire. Closing requires explicit dismiss
// so they don't lose the password before sharing.
function CredentialsNotice({ notice, onClose }) {
  const [copied, setCopied] = useState(false);
  const text = `Employee ID: ${notice.empId}\nPassword: ${notice.password}\nLogin at: app.nycmanufacturing.com`;
  const copy = () => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 310,
        background: "rgba(10,10,15,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 96vw)",
          background: "#F4F2EC", border: "2px solid #0A0A0F",
          boxShadow: "8px 8px 0 #0033FF",
        }}
      >
        <div style={{
          padding: "18px 22px", borderBottom: "2px solid #0A0A0F",
        }}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
            letterSpacing: "0.12em", textTransform: "uppercase",
            color: "#0033FF", fontWeight: 500, marginBottom: 6,
          }}>· ACCOUNT CREATED ·</div>
          <h2 style={{
            fontFamily: "'Archivo Black', 'Archivo', sans-serif",
            fontSize: 22, fontWeight: 900, textTransform: "uppercase",
            letterSpacing: "-0.02em", lineHeight: 1, margin: 0, color: "#0A0A0F",
          }}>{notice.name}'s login</h2>
        </div>
        <div style={{ padding: 22 }}>
          <div style={{
            background: "#FFFFFF", border: "2px solid #0A0A0F", padding: 16,
            fontFamily: "'JetBrains Mono', monospace", fontSize: 14,
            lineHeight: 1.8, color: "#0A0A0F",
          }}>
            <div><span style={{ color: "rgba(10,10,15,0.58)" }}>ID</span> &nbsp; <strong>{notice.empId}</strong></div>
            <div><span style={{ color: "rgba(10,10,15,0.58)" }}>PW</span> &nbsp; <strong>{notice.password}</strong></div>
            <div><span style={{ color: "rgba(10,10,15,0.58)" }}>ROLE</span> &nbsp; <strong>{notice.role}</strong></div>
          </div>
          <p style={{
            fontFamily: "'Archivo', sans-serif", fontSize: 13, color: "rgba(10,10,15,0.58)",
            marginTop: 14, lineHeight: 1.5,
          }}>
            Share these with {notice.name.split(" ")[0]} so they can sign in. You can
            change their permissions in Settings.
          </p>
        </div>
        <div style={{
          padding: "14px 22px", borderTop: "2px solid #0A0A0F",
          display: "flex", gap: 10, justifyContent: "flex-end",
        }}>
          <button
            type="button"
            onClick={copy}
            style={{
              padding: "10px 18px", border: "2px solid #0A0A0F",
              background: "transparent", color: "#0A0A0F", cursor: "pointer",
              fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13,
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}
          >{copied ? "Copied!" : "Copy"}</button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 18px", border: "2px solid #0033FF",
              background: "#0033FF", color: "#FFFFFF", cursor: "pointer",
              fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 13,
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}
          >Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
